import { FC, useState, useMemo } from 'react';
// import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import { useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { Grid, Paper, Typography, Card, CardContent, Select, MenuItem, Tooltip } from '@material-ui/core';
import { GridColumns, DataGrid, GridRowParams, GridCellParams } from '@material-ui/data-grid';
import GroupIcon from '@material-ui/icons/Group';
import BusinessIcon from '@material-ui/icons/Business';
import { Search } from 'components/Search';
import { getRowsPerPageOptions } from 'utils/pagination';
import { useStyles, BootstrapInput } from './styles';
import { ManagedEntity, ApiResponse } from './types';
const Manager: FC = () => {
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedManager, setSelectedManager] = useState<string>('');
  const [pageSize, setPageSize] = useState(10);
  const {
    logsTableStore: { getMangerReports, getManagers },
    userStore: { user },
    notificationStore: { openNotification },
  } = useStore();
  const classes = useStyles();
  const getMangerReportsReq = useQuery<ApiResponse>(
    ['getMangerReportsReq', selectedManager],
    () => {
      const managerId = selectedManager ? managerData.find((m) => m.name === selectedManager)?.id : undefined;
      return getMangerReports(managerId);
    },
    {
      retry: 1,
      refetchOnWindowFocus: false,
      onError: (e: any) => {
        console.error('Manager reports error:', e);
        openNotification('error', e?.message || `Can't Get Manager Reports`);
      },
    }
  );
  const getManagersReq = useQuery<ApiResponse>('getManagersReq', () => getManagers(), {
    enabled: user?.permission !== 'EndUser' && user?.permission !== 'Manager',
    retry: 1,
    refetchOnWindowFocus: false,

    onError: (e: any) => {
      console.error('Manager reports error:', e);
      openNotification('error', e?.message || `Can't Get Manager Reports`);
    },
  });

  const data = getMangerReportsReq.data;
  const managerData = getManagersReq.data?.data?.managers || [];

  const groups = useMemo(() => {
    return data?.data?.managed_entities.filter((entity: ManagedEntity) => entity.type === 'Group') || [];
  }, [data?.data?.managed_entities]);

  const departments = useMemo(() => {
    return data?.data?.managed_entities.filter((entity: ManagedEntity) => entity.type === 'Department') || [];
  }, [data?.data?.managed_entities]);

  // const totalUsers = useMemo(() => {
  //   return data?.data?.managed_entities.reduce((sum: number, entity: ManagedEntity) => sum + entity.user_count, 0) || 0;
  // }, [data?.data?.managed_entities]);

  // const allApps = useMemo(() => {
  //   return [...new Set(data?.data?.managed_entities.flatMap((entity: ManagedEntity) => entity.apps_assigned) || [])];
  // }, [data?.data?.managed_entities]);

  const allApps = useMemo(() => {
    const apps = data?.data?.managed_entities.flatMap((entity: ManagedEntity) => entity.apps_assigned) || [];

    return Array.from(new Set(apps));
  }, [data?.data?.managed_entities]);
  const rows = useMemo(() => {
    if (!data?.data?.managed_entities) return [];

    let filteredEntities = data.data.managed_entities;

    // Filter by type
    if (typeFilter && typeFilter !== 'all') {
      filteredEntities = filteredEntities.filter((entity: ManagedEntity) => entity.type.toLowerCase() === typeFilter);
    }

    // Filter by search term
    if (filter && filter.trim() !== '') {
      const searchTerm = filter.toLowerCase().trim();
      filteredEntities = filteredEntities.filter((entity: ManagedEntity) => {
        const searchableText = `${entity.name} ${entity.type} ${entity.apps_assigned.join(' ')}`.toLowerCase();
        return searchableText.includes(searchTerm);
      });
    }

    return filteredEntities.map((entity: ManagedEntity) => ({
      ...entity,
    }));
  }, [data?.data?.managed_entities, filter, typeFilter]);

  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);

  useAppPreloader(getMangerReportsReq.isFetching);

  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 50,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.type} placement='top'>
          {params.row.type === 'Group' ? (
            <GroupIcon style={{ color: 'grey' }} />
          ) : (
            <BusinessIcon style={{ color: 'grey' }} />
          )}
        </Tooltip>
      ),
    },
    {
      field: 'type',
      headerName: 'TYPE',
      flex: 1,
    },
    { field: 'name', headerName: 'FULL NAME', flex: 2 },
    {
      field: 'user_count',
      headerName: 'USER COUNT',
      flex: 1,
    },
    {
      field: 'apps_assigned',
      headerName: 'CONNECTED APPS',
      flex: 2,
      renderCell: (params: GridCellParams) => {
        const apps = (params.value as string[]) ?? [];

        return (
          <Typography variant='body2' color='textSecondary'>
            {apps.length > 0
              ? apps.map((app) => app.charAt(0).toUpperCase() + app.slice(1)).join(', ')
              : 'No apps connected'}
          </Typography>
        );
      },
    },
  ];
  return (
    <>
      <Grid container justify='space-between' alignItems='center' className={classes.box}>
        <div className={classes.flex}>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as string)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected || selected === 'all') {
                return 'All Types';
              }
              const value = selected as string;
              return value.charAt(0).toUpperCase() + value.slice(1) + 's';
              // return selected.charAt(0).toUpperCase() + selected.slice(1) + 's';
            }}
            input={<BootstrapInput />}
          >
            <MenuItem value='all'>All Types</MenuItem>
            <MenuItem value='group'>Groups</MenuItem>
            <MenuItem value='department'>Departments</MenuItem>
          </Select>
          {user?.permission !== 'EndUser' && user?.permission !== 'Manager' && (
            <Select
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value as string)}
              displayEmpty
              renderValue={(selected) => {
                const value = selected as string;
                return value || 'Select Manager';
              }}
              input={<BootstrapInput />}
            >
              <MenuItem value=''>All Managers</MenuItem>
              {managerData.map((manager) => (
                <MenuItem key={manager.id} value={manager.name}>
                  {manager.name}
                </MenuItem>
              ))}
            </Select>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            gap: '8px',
            width: 'auto',
            justifyContent: 'flex-end',
          }}
        >
          <Search handelSearch={setFilter} />
        </div>
      </Grid>
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2} className={classes.summaryGrid}>
          <Grid item xs={12} sm={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsLabel}>Groups</Typography>
                <Typography className={classes.statsValue}>{groups.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsLabel}>Departments</Typography>
                <Typography className={classes.statsValue}>{departments.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsLabel}>Connected Apps</Typography>
                <Typography className={classes.statsValue}>{allApps.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <div className={classes.dataGrid}>
          <DataGrid
            className={classes.noBorder}
            autoHeight
            rows={rows}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={rowsPerPageOptions}
            pagination
            onPageSizeChange={(params) => setPageSize(params.pageSize)}
            disableSelectionOnClick
            getRowClassName={(params: GridRowParams) => `row-background-${params.row.type?.toLowerCase() || 'default'}`}
            density={'standard'}
          />
        </div>
      </Paper>
    </>
  );
};

export default Manager;
